import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'
import * as XLSX from 'xlsx'

/* ─── design tokens ─── */
const T = {
    bg:'#f1f5f9', card:'#ffffff', border:'#e8edf5',
    blue:'#3b82f6', blueD:'#2563eb', navy:'#0f172a',
    txt:'#1e293b', txt2:'#64748b', muted:'#94a3b8',
    green:'#16a34a', greenBg:'#f0fdf4', greenBdr:'#bbf7d0',
    red:'#dc2626', redBg:'#fef2f2', redBdr:'#fecaca',
    warn:'#f59e0b', warnBg:'#fffbeb', warnBdr:'#fde68a',
    r:16, rsm:10,
    shadow:'0 1px 3px rgba(15,23,42,.06)',
    font:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
}

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

const EXCLUIDOS = ['VENDIDA','NO REVISION','LIQUIDADA']
const EXCLUIDOS_FULL = [...EXCLUIDOS, 'SUSPENDIDA']

function datediff(a, b) { return Math.round((a - b) / (1000 * 60 * 60 * 24)) }
function fmt(v) {
    if (v === null || v === undefined || v === '') return '—'
    if (isNaN(v)) return '—'
    const n = Number(v)
    if (n === 0) return '—'
    return n.toLocaleString('es-MX', { minimumFractionDigits:2, maximumFractionDigits:2 })
}

/* ─── MultiSelect ─── */
function MultiSelect({ label, options, value, onChange, getLabel, getId, placeholder='Todos' }) {
    const [open,  setOpen]  = useState(false)
    const [query, setQuery] = useState('')
    const inputRef     = useRef(null)
    const containerRef = useRef(null)
    useEffect(() => {
        const h = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', h)
        return () => document.removeEventListener('mousedown', h)
    }, [])
    const filtered   = options.filter(o => getLabel(o).toLowerCase().includes(query.toLowerCase()))
    const isSelected = o => value.some(v => getId(v) === getId(o))
    const toggle     = o => onChange(isSelected(o) ? value.filter(v => getId(v) !== getId(o)) : [...value, o])
    return (
        <div ref={containerRef} style={{ position:'relative', flex:1, minWidth:160 }}>
            {label && <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.txt2, marginBottom:4, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</label>}
            <div onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 50) }}
                style={{ minHeight:38, padding:'4px 10px', borderRadius:T.rsm, border:`1px solid ${open ? T.blue : T.border}`,
                    background:T.card, cursor:'pointer', display:'flex', flexWrap:'wrap', gap:4, alignItems:'center',
                    boxShadow: open ? `0 0 0 3px ${T.blue}20` : T.shadow, transition:'all .15s' }}>
                {value.length === 0 && !open && <span style={{ fontSize:13, color:T.muted }}>{placeholder}</span>}
                {value.map(v => (
                    <span key={getId(v)} style={{ display:'inline-flex', alignItems:'center', gap:4, background:`${T.blue}12`,
                        border:`1px solid ${T.blue}30`, borderRadius:6, padding:'1px 7px', fontSize:12, color:T.blueD, fontWeight:500 }}>
                        {getLabel(v)}
                        <span onMouseDown={e => { e.preventDefault(); e.stopPropagation(); toggle(v) }}
                            style={{ cursor:'pointer', color:T.blue, fontWeight:700 }}>×</span>
                    </span>
                ))}
                {open && <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                    onMouseDown={e => e.stopPropagation()} placeholder="Buscar..." autoComplete="off"
                    style={{ border:'none', outline:'none', fontSize:13, color:T.txt, background:'transparent', minWidth:80, flex:1, fontFamily:T.font }} />}
            </div>
            {open && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, marginTop:4,
                    background:T.card, border:`1px solid ${T.border}`, borderRadius:T.rsm,
                    boxShadow:'0 8px 24px rgba(15,23,42,.12)', maxHeight:220, overflowY:'auto' }}>
                    {filtered.length === 0
                        ? <div style={{ padding:'10px 14px', fontSize:13, color:T.muted }}>Sin resultados</div>
                        : filtered.map(o => (
                            <div key={getId(o)} onMouseDown={e => { e.preventDefault(); toggle(o) }}
                                style={{ padding:'9px 14px', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:8,
                                    background: isSelected(o) ? `${T.blue}08` : 'transparent',
                                    color: isSelected(o) ? T.blue : T.txt, fontWeight: isSelected(o) ? 600 : 400 }}>
                                <span style={{ width:15, height:15, borderRadius:4, border:`1.5px solid ${isSelected(o) ? T.blue : T.border}`,
                                    background: isSelected(o) ? T.blue : 'transparent', display:'inline-flex',
                                    alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                    {isSelected(o) && <span style={{ color:'white', fontSize:10, fontWeight:800 }}>✓</span>}
                                </span>
                                {getLabel(o)}
                            </div>
                        ))
                    }
                </div>
            )}
        </div>
    )
}

/* ─── Toggle Switch ─── */
function Toggle({ label, checked, onChange }) {
    return (
        <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', userSelect:'none' }}>
            <div onClick={() => onChange(!checked)} style={{ position:'relative', width:40, height:22, borderRadius:11,
                background: checked ? T.blue : T.border, transition:'background .2s', flexShrink:0 }}>
                <div style={{ position:'absolute', top:3, left: checked ? 21 : 3, width:16, height:16,
                    borderRadius:'50%', background:'white', transition:'left .2s',
                    boxShadow:'0 1px 3px rgba(0,0,0,.2)' }} />
            </div>
            <span style={{ fontSize:13, color:T.txt2 }}>{label}</span>
        </label>
    )
}

/* ─── Chip de mensaje (alerta días) ─── */
function AlertChip({ msg }) {
    const isRed = msg.includes('90+')
    return (
        <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700, marginRight:4,
            background: isRed ? T.redBg : T.warnBg,
            border: `1px solid ${isRed ? T.redBdr : T.warnBdr}`,
            color: isRed ? T.red : T.warn }}>
            {msg}
        </span>
    )
}

export default function RevisionPlazaMensual() {
    const history = useHistory()
    const { id }  = useParams()

    // catálogos
    const [allClientes,   setAllClientes]   = useState([])
    const [allSociedades, setAllSociedades] = useState([])
    const [allResp,       setAllResp]       = useState([])
    const [allAux,        setAllAux]        = useState([])

    // filtros
    const [fClientes,   setFClientes]   = useState([])
    const [fSociedades, setFSociedades] = useState([])
    const [fResp,       setFResp]       = useState([])
    const [fAux,        setFAux]        = useState([])

    // periodo / ejercicio
    const defaultEjercicio = new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear()
    const defaultPeriodo   = MESES[new Date().getMonth() === 0 ? 11 : new Date().getMonth() - 1]
    const [ejercicio, setEjercicio] = useState(defaultEjercicio)
    const [periodo,   setPeriodo]   = useState(defaultPeriodo)

    // opciones display
    const [mostrarVendidas,    setMostrarVendidas]    = useState(false)
    const [soloPendientes,     setSoloPendientes]     = useState(false)

    // datos
    const [declaraciones, setDeclaraciones] = useState([])
    const [loading,       setLoading]       = useState(false)

    // paginación
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 50

    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('READ_CONTABILIDAD')) {
            history.push('/login'); return
        }
        if (Number(id) === -1) return
        Promise.all([
            ElementsDataService.retrieveAllElements('clientes'),
            ElementsDataService.retrieveAllElements('sociedades'),
            ElementsDataService.retrieveAllElements('responsablesContabilidad'),
            ElementsDataService.retrieveAllElements('auxiliaresContables'),
        ]).then(([cli, soc, resp, aux]) => {
            setAllClientes([{ id:0, nombreCliente:'TODOS' }, ...(cli.data || [])])
            setAllSociedades(soc.data || [])
            setAllResp(resp.data || [])
            setAllAux(aux.data || [])
        })
    }, []) // eslint-disable-line

    const buscar = useCallback((clts, ejr, per, aux, resp, soc) => {
        const clientesIds   = clts.map(c => c.id)
        const sociedadesIds = soc.map(s => s.id)
        const auxCodes      = aux.map(a => a.codigo)
        const respCodes     = resp.map(r => r.codigo)

        if (!clientesIds.length && !sociedadesIds.length && !auxCodes.length && !respCodes.length) {
            setDeclaraciones([]); return
        }
        const formData = new FormData()
        formData.append('clientesIds',               clientesIds)
        formData.append('sociedadesIds',             sociedadesIds)
        formData.append('auxiliaresContablesCodes',  auxCodes)
        formData.append('responsablesContabilidadCodes', respCodes)

        setLoading(true)
        ElementsDataService.retrieveElementPost(`cfdis/declaraciones/${ejr}/${per}`, formData)
            .then(r => { setDeclaraciones(r.data || []); setPage(1) })
            .finally(() => setLoading(false))
    }, [])

    /* ── construir filas ── */
    const periodoIdx            = MESES.indexOf(periodo)
    const currentDeclaracionDate = new Date(ejercicio, periodoIdx, new Date().getDate())

    const rows = []
    let consecutivo = 1

    declaraciones.forEach(cliente => {
        cliente.sociedadesdeclaraciones?.forEach(sociedad => {
            const excluida = EXCLUIDOS.includes(sociedad.responsableContabilidad) ||
                             EXCLUIDOS.includes(sociedad.statusContabilidad)
            if (!mostrarVendidas && excluida) return

            const mensajes = []

            sociedad.declaracionesPeriodoEjercicio?.forEach(declaracion => {
                if (declaracion) {
                    const declPeriodoIdx = MESES.indexOf(declaracion.periodo)
                    const declDate       = new Date(declaracion.ejercicio, declPeriodoIdx, 22)
                    const dias           = datediff(currentDeclaracionDate, declDate)
                    const excluida2      = EXCLUIDOS_FULL.includes(sociedad.responsableContabilidad) ||
                                          EXCLUIDOS_FULL.includes(sociedad.statusContabilidad)
                    if (!excluida2) {
                        if (dias > 90) mensajes.push('90+ días última declaración')
                        else if (dias > 60) mensajes.push('60+ días última declaración')
                    }
                }
            })

            if (soloPendientes && mensajes.length === 0) return

            const decl = sociedad.declaracionesPeriodoEjercicio?.find(d => d?.periodo === periodo) || null

            rows.push({ consecutivo: consecutivo++, cliente, sociedad, decl, mensajes })
        })
    })

    const totalPages = Math.ceil(rows.length / PAGE_SIZE)
    const paginated  = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    /* ── Excel export ── */
    const downloadExcel = () => {
        const data = rows.map(({ consecutivo, cliente, sociedad, decl, mensajes }) => {
            const complementarias = decl?.declaracionesProvisonalesDelPeriodoEjercicio
                ?.map(d => `${d.tipoDeclaracion} ${d.fechaHora}`).join(' | ') || ''
            return {
                'N°':                          consecutivo,
                'Mensajes':                    mensajes.join(' | '),
                'Proyecto':                    cliente.nombreCliente,
                'Razón Social':                sociedad.razonSocial,
                'RFC':                         sociedad.rfc,
                'Propietario':                 cliente.propietario,
                'Responsable Contabilidad':    sociedad.responsableContabilidad,
                'Auxiliar Contable':           sociedad.auxiliarContable,
                'Fecha PPM':                   decl?.fechaPresentacionPPM || '',
                'Última Declaración':          decl ? `${decl.periodo}/${decl.ejercicio} ${decl.fechaUltimaPresentacionPeriodoEjercicio||''}` : '',
                'Complementarias':             complementarias,
                "CFDIs Tipo I Metadata":       decl?.cfdisIngresosMetadata ?? '',
                "CFDIs Tipo I Acum":           decl?.cfdisIngresosAcumuladosMetadata ?? '',
                "CFDIs Tipo N Metadata":       decl?.cfdisNominaMetadata ?? '',
                "CFDIs Tipo N Acum":           decl?.cfdisNominaAcumuladaMetadata ?? '',
                "Ingresos SAT":                decl?.totalIngresosNominales ?? '',
                "Ingresos Acum SAT":           decl?.ingresosNominalesMesDeclarado ?? '',
                "R1 ISR":                      decl?.r1Acargo ?? '',
                "R1 Recargos":                 decl?.r1Recargos ?? '',
                "R1 Total Pagar":              decl?.r1CantidadAPagar ?? '',
                "R12 Sueldos":                 decl?.r12Acargo ?? '',
                "R12 Recargos":                decl?.r12Recargos ?? '',
                "R12 Subsidio":                decl?.r12Subsidio ?? '',
                "R12 Total Pagar":             decl?.r12CantidadAPagar ?? '',
                "R13 ISR IAS":                 decl?.r13Acargo ?? '',
                "R13 Recargos":                decl?.r13Recargos ?? '',
                "R13 Subsidio":                decl?.r13Subsidio ?? '',
                "R13 Total Pagar":             decl?.r13CantidadAPagar ?? '',
                "R14 ISR Ret SP":              decl?.r14Acargo ?? '',
                "R14 Recargos":                decl?.r14Recargos ?? '',
                "R14 Total Pagar":             decl?.r14CantidadAPagar ?? '',
                "R15 ISR Ret Arrend":          decl?.r15Acargo ?? '',
                "R15 Recargos":                decl?.r15Recargos ?? '',
                "R15 Total Pagar":             decl?.r15CantidadAPagar ?? '',
                "R21 IVA Cargo":               decl?.r21Acargo ?? '',
                "R21 IVA Favor":               decl?.r21Afavor ?? '',
                "R21 Recargos":                decl?.r21Recargos ?? '',
                "R21 Total Pagar":             decl?.r21CantidadAPagar ?? '',
                "R21 IVA RFN Cargo":           decl?.r21AcargoRFN ?? '',
                "R21 IVA RFN Favor":           decl?.r21AfavorRFN ?? '',
                "R21 RFN Recargos":            decl?.r21RecargosRFN ?? '',
                "R21 RFN Total Pagar":         decl?.r21CantidadAPagarRFN ?? '',
                "R24 IVA Ret":                 decl?.r24Acargo ?? '',
                "R24 Recargos":                decl?.r24Recargos ?? '',
                "R24 Total Pagar":             decl?.r24CantidadAPagar ?? '',
                "IVA SC Cargo":                decl?.ivaSimplificadoConfianzaAcargo ?? '',
                "IVA SC Favor":                decl?.ivaSimplificadoConfianzaAfavor ?? '',
                "IVA SC Recargos":             decl?.ivaSimplificadoConfianzaRecargos ?? '',
                "IVA SC Total":                decl?.ivaSimplificadoConfianzaCantidadAPagar ?? '',
                "ISR SC":                      decl?.isrSimplificadoConfianzaAcargo ?? '',
                "ISR SC Recargos":             decl?.isrSimplificadoConfianzaRecargos ?? '',
                "ISR SC Total":                decl?.isrSimplificadoConfianzaCantidadAPagar ?? '',
            }
        })
        const ws = XLSX.utils.json_to_sheet(data)
        // anchos de columna
        const fixedCols = [4, 6, 8, 6, 30, 20, 16, 14, 22, 22, 16, 30, 14]
        ws['!cols'] = Object.keys(data[0] || {}).map((k, i) => ({ wch: fixedCols[i] || 14 }))
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, `${periodo} ${ejercicio}`)
        XLSX.writeFile(wb, `Revision_Plaza_${periodo}_${ejercicio}_${new Date().toISOString().slice(0,10)}.xlsx`)
    }

    /* ── estilos tabla ── */
    const thG = (bg, cols) => ({ padding:'8px 10px', fontSize:10, fontWeight:800, color:'white',
        background:bg, textAlign:'center', borderRight:`1px solid rgba(255,255,255,.2)`,
        letterSpacing:'.04em', whiteSpace:'nowrap' })
    const thS = { padding:'8px 10px', fontSize:10, fontWeight:700, color:T.txt2,
        textTransform:'uppercase', letterSpacing:'.04em', whiteSpace:'nowrap',
        borderBottom:`2px solid ${T.border}`, background:'#f8fafc', textAlign:'left',
        borderRight:`1px solid ${T.border}` }
    const tdS = { padding:'8px 10px', fontSize:12, color:T.txt, borderBottom:`1px solid ${T.border}`,
        borderRight:`1px solid ${T.border}`, verticalAlign:'middle', whiteSpace:'nowrap' }
    const tdR = { ...tdS, textAlign:'right', fontVariantNumeric:'tabular-nums', fontFamily:'monospace', fontSize:11 }
    const linkSt = { color:T.blue, textDecoration:'none', fontWeight:500 }

    /* ── grupos de columnas ── */
    const groups = [
        { label:'INFORMACIÓN GENERAL', cols:8, bg:'#3b82f6' },
        { label:'FECHAS', cols:3, bg:'#2563eb' },
        { label:`CONTABLE ${periodo}-${ejercicio}`, cols:6, bg:'#3b82f6' },
        { label:'R1 ISR', cols:3, bg:'#7c3aed' },
        { label:'R12 ISR SUE. Y SAL.', cols:4, bg:'#6d28d9' },
        { label:'R13 ISR IAS', cols:4, bg:'#7c3aed' },
        { label:'R14 ISR RET. SP', cols:3, bg:'#6d28d9' },
        { label:'R15 ISR RET. ARR.', cols:3, bg:'#7c3aed' },
        { label:'R21 IVA', cols:4, bg:'#0891b2' },
        { label:'R21 IVA RFN', cols:4, bg:'#0e7490' },
        { label:'R24 IVA RET.', cols:3, bg:'#0891b2' },
        { label:'IVA SIMP. CONFIANZA', cols:4, bg:'#059669' },
        { label:'ISR SIMP. CONFIANZA', cols:3, bg:'#047857' },
    ]

    if (loading) return (
        <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:T.font }}>
            <div style={{ textAlign:'center' }}>
                <div style={{ width:36, height:36, border:`3px solid ${T.border}`, borderTopColor:T.blue,
                    borderRadius:'50%', animation:'spin .7s linear infinite', margin:'0 auto 12px' }} />
                <p style={{ color:T.txt2, fontSize:14, margin:0 }}>Cargando datos...</p>
            </div>
            <style>{`@keyframes spin { to { transform:rotate(360deg) } }`}</style>
        </div>
    )

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'24px 28px 64px' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                @keyframes spin { to { transform:rotate(360deg) } }
                tr:hover td { background: #eff6ff !important; }
            `}</style>

            <div style={{ maxWidth:'100%' }}>

                {/* ── Header ── */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <button onClick={() => history.goBack()}
                            style={{ width:34, height:34, borderRadius:9, border:`1px solid ${T.border}`,
                                background:T.card, cursor:'pointer', fontSize:16, color:T.txt2,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                boxShadow:T.shadow, flexShrink:0, fontFamily:T.font, transition:'all .15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background=T.navy; e.currentTarget.style.color='white' }}
                            onMouseLeave={e => { e.currentTarget.style.background=T.card; e.currentTarget.style.color=T.txt2 }}>
                            ‹
                        </button>
                        <div>
                            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:T.navy, letterSpacing:'-0.025em' }}>
                                Revisión Proyectos Mensual
                            </h1>
                            <p style={{ margin:'3px 0 0', fontSize:13, color:T.txt2 }}>
                                {periodo} {ejercicio} · {rows.length} sociedades
                            </p>
                        </div>
                    </div>
                    <button onClick={downloadExcel} disabled={rows.length === 0}
                        style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'9px 18px',
                            borderRadius:T.rsm, border:'none', background: rows.length === 0 ? T.muted : T.blue,
                            color:'white', fontSize:13, fontWeight:700, cursor: rows.length === 0 ? 'not-allowed':'pointer',
                            fontFamily:T.font, boxShadow: rows.length > 0 ? `0 4px 12px ${T.blue}40`:'none' }}>
                        📥 Descargar Excel
                    </button>
                </div>

                {/* ── Panel de filtros ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, padding:'16px 20px', marginBottom:16 }}>
                    {/* Fila 1: clientes + ejercicio + periodo */}
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:12, alignItems:'flex-end' }}>
                        <div style={{ flex:2, minWidth:200 }}>
                            <MultiSelect label="Proyectos" options={allClientes} value={fClientes}
                                onChange={v => { setFClientes(v); buscar(v, ejercicio, periodo, fAux, fResp, fSociedades) }}
                                getLabel={o => o.nombreCliente} getId={o => o.id} />
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <label style={{ fontSize:11, fontWeight:600, color:T.txt2, textTransform:'uppercase', letterSpacing:'.05em' }}>Ejercicio</label>
                            <input type="number" min="1900" max="2099" step="1" value={ejercicio}
                                onChange={e => { const v = Number(e.target.value); setEjercicio(v); buscar(fClientes, v, periodo, fAux, fResp, fSociedades) }}
                                style={{ width:90, padding:'8px 10px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                    fontSize:13, color:T.txt, fontFamily:T.font, outline:'none', background:T.card }} />
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <label style={{ fontSize:11, fontWeight:600, color:T.txt2, textTransform:'uppercase', letterSpacing:'.05em' }}>Periodo</label>
                            <select value={periodo}
                                onChange={e => { setPeriodo(e.target.value); buscar(fClientes, ejercicio, e.target.value, fAux, fResp, fSociedades) }}
                                style={{ padding:'8px 10px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                    fontSize:13, color:T.txt, fontFamily:T.font, background:T.card, cursor:'pointer', outline:'none' }}>
                                {MESES.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                    </div>
                    {/* Fila 2: sociedades + responsable + auxiliar */}
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:14 }}>
                        <MultiSelect label="Sociedades" options={allSociedades} value={fSociedades}
                            onChange={v => { setFSociedades(v); buscar(fClientes, ejercicio, periodo, fAux, fResp, v) }}
                            getLabel={o => `${o.razonSocial} ${o.rfc}`} getId={o => o.rfc} />
                        <MultiSelect label="Responsable Contabilidad" options={allResp} value={fResp}
                            onChange={v => { setFResp(v); buscar(fClientes, ejercicio, periodo, fAux, v, fSociedades) }}
                            getLabel={o => o.codigo} getId={o => o.codigo} />
                        <MultiSelect label="Auxiliar Contable" options={allAux} value={fAux}
                            onChange={v => { setFAux(v); buscar(fClientes, ejercicio, periodo, v, fResp, fSociedades) }}
                            getLabel={o => o.codigo} getId={o => o.codigo} />
                    </div>
                    {/* Fila 3: toggles */}
                    <div style={{ display:'flex', gap:24, flexWrap:'wrap', paddingTop:12, borderTop:`1px solid ${T.border}` }}>
                        <Toggle label="Mostrar Vendidas / No Revisión / Liquidadas"
                            checked={mostrarVendidas} onChange={setMostrarVendidas} />
                        <Toggle label="Mostrar solo sociedades con pendientes"
                            checked={soloPendientes} onChange={setSoloPendientes} />
                    </div>
                </div>

                {/* ── Tabla ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, overflow:'hidden' }}>
                    {/* cabecera tabla */}
                    <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`,
                        display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span>📊</span>
                            <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:T.txt }}>Resultados</h2>
                            <span style={{ fontSize:12, fontWeight:700, color:T.blue, background:`${T.blue}12`,
                                border:`1px solid ${T.blue}20`, borderRadius:10, padding:'1px 8px' }}>{rows.length}</span>
                        </div>
                        {rows.length > PAGE_SIZE && (
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                                    style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${T.border}`,
                                        background: page===1 ? T.bg : T.card, color: page===1 ? T.muted : T.txt,
                                        cursor: page===1 ? 'default':'pointer', fontSize:13, fontFamily:T.font }}>‹</button>
                                <span style={{ fontSize:12, color:T.txt2, whiteSpace:'nowrap' }}>
                                    {page} / {totalPages}
                                    <span style={{ color:T.muted }}> ({(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE, rows.length)} de {rows.length})</span>
                                </span>
                                <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}
                                    style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${T.border}`,
                                        background: page===totalPages ? T.bg : T.card, color: page===totalPages ? T.muted : T.txt,
                                        cursor: page===totalPages ? 'default':'pointer', fontSize:13, fontFamily:T.font }}>›</button>
                            </div>
                        )}
                    </div>

                    {rows.length === 0 ? (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ fontSize:40, marginBottom:10 }}>🔍</div>
                            <p style={{ margin:0, fontSize:14, fontWeight:600, color:T.txt }}>
                                {declaraciones.length === 0 ? 'Selecciona filtros y presiona buscar' : 'Sin resultados con los filtros actuales'}
                            </p>
                        </div>
                    ) : (
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ borderCollapse:'collapse', fontSize:12, minWidth:2200 }}>
                                <thead>
                                    {/* Fila de grupos */}
                                    <tr>
                                        {groups.map((g, i) => (
                                            <th key={i} colSpan={g.cols} style={thG(g.bg)}>
                                                {g.label}
                                            </th>
                                        ))}
                                    </tr>
                                    {/* Fila de columnas individuales */}
                                    <tr>
                                        {['N°','Mensajes','Proyecto','Razón Social','RFC','Propietario','Resp. Contabilidad','Aux. Contable',
                                          'Fecha PPM','Última Declaración','Complementarias',
                                          `CFDIs I ${periodo}`,`CFDIs I Acum ${ejercicio}`,`CFDIs N ${periodo}`,`CFDIs N Acum ${ejercicio}`,`Ing. SAT ${periodo}`,`Ing. Acum SAT ${ejercicio}`,
                                          'ISR PM','Recargos','Total Pagar',
                                          'ISR Sue.','Recargos','Subsidio','Total',
                                          'ISR IAS','Recargos','Subsidio','Total',
                                          'ISR Ret SP','Recargos','Total',
                                          'ISR Ret Arr','Recargos','Total',
                                          'IVA Cargo','IVA Favor','Recargos','Total',
                                          'IVA RFN Cargo','IVA RFN Favor','Recargos','Total',
                                          'IVA Ret','Recargos','Total',
                                          'IVA SC Cargo','IVA SC Favor','Recargos','Total',
                                          'ISR SC','Recargos','Total',
                                        ].map(h => <th key={h} style={thS}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map(({ consecutivo, cliente, sociedad, decl, mensajes }, i) => {
                                        const rowBg = mensajes.length > 0 ? T.warnBg : (i % 2 === 0 ? T.card : T.bg)
                                        const d = decl
                                        return (
                                            <tr key={`${sociedad.rfc}-${i}`}>
                                                <td style={{ ...tdS, background:rowBg, textAlign:'center', fontWeight:700, color:T.muted }}>{consecutivo}</td>
                                                <td style={{ ...tdS, background:rowBg, maxWidth:180 }}>
                                                    {mensajes.map((m, mi) => <AlertChip key={mi} msg={m} />)}
                                                </td>
                                                <td style={{ ...tdS, background:rowBg }}>
                                                    <Link to={`/clientes/reportes/${cliente.clienteId}`} style={linkSt}>{cliente.nombreCliente}</Link>
                                                </td>
                                                <td style={{ ...tdS, background:rowBg, fontWeight:500, minWidth:160 }}>
                                                    <Link to={`/sociedades/reportes/${sociedad.id}`} style={linkSt}>{sociedad.razonSocial}</Link>
                                                </td>
                                                <td style={{ ...tdS, background:rowBg, fontFamily:'monospace' }}>
                                                    <Link to={`/sociedades/reportesContables/${sociedad.rfc}`} style={linkSt}>{sociedad.rfc}</Link>
                                                </td>
                                                <td style={{ ...tdS, background:rowBg }}>{cliente.propietario || '—'}</td>
                                                <td style={{ ...tdS, background:rowBg }}>{sociedad.responsableContabilidad || '—'}</td>
                                                <td style={{ ...tdS, background:rowBg }}>{sociedad.auxiliarContable || '—'}</td>
                                                <td style={{ ...tdS, background:rowBg }}>{d?.fechaPresentacionPPM || '—'}</td>
                                                <td style={{ ...tdS, background:rowBg }}>
                                                    {d ? <><span style={{ fontWeight:600 }}>{d.periodo}/{d.ejercicio}</span><br/><span style={{ color:T.txt2 }}>{d.fechaUltimaPresentacionPeriodoEjercicio}</span></> : '—'}
                                                </td>
                                                <td style={{ ...tdS, background:rowBg, maxWidth:200 }}>
                                                    {d?.declaracionesProvisonalesDelPeriodoEjercicio?.map((dp, dpi) => (
                                                        <div key={dpi} style={{ marginBottom:4 }}>
                                                            <span style={{ fontSize:11, color:T.txt2 }}>{dp.tipoDeclaracion} · {dp.fechaHora}</span>
                                                            {dp.documentFileUrl && (
                                                                <button onClick={() => ElementsDataService.downloadS3File(
                                                                    `?fileName=${dp.documentFileUrl}`,
                                                                    `${sociedad.rfc}_${dp.tipoDeclaracion}_${d.periodo}_${d.ejercicio}.pdf`
                                                                )} style={{ marginLeft:6, padding:'1px 8px', borderRadius:5, border:`1px solid ${T.greenBdr}`,
                                                                    background:T.greenBg, color:T.green, fontSize:11, cursor:'pointer', fontFamily:T.font }}>
                                                                    ⬇
                                                                </button>
                                                            )}
                                                        </div>
                                                    )) || '—'}
                                                </td>
                                                {/* Contable */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.cfdisIngresosMetadata)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.cfdisIngresosAcumuladosMetadata)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.cfdisNominaMetadata)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.cfdisNominaAcumuladaMetadata)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.totalIngresosNominales)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.ingresosNominalesMesDeclarado)}</td>
                                                {/* R1 */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r1Acargo)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r1Recargos)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r1CantidadAPagar)}</td>
                                                {/* R12 */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r12Acargo)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r12Recargos)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r12Subsidio)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r12CantidadAPagar)}</td>
                                                {/* R13 */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r13Acargo)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r13Recargos)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r13Subsidio)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r13CantidadAPagar)}</td>
                                                {/* R14 */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r14Acargo)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r14Recargos)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r14CantidadAPagar)}</td>
                                                {/* R15 */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r15Acargo)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r15Recargos)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r15CantidadAPagar)}</td>
                                                {/* R21 */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r21Acargo)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r21Afavor)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r21Recargos)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r21CantidadAPagar)}</td>
                                                {/* R21 RFN */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r21AcargoRFN)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r21AfavorRFN)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r21RecargosRFN)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r21CantidadAPagarRFN)}</td>
                                                {/* R24 */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r24Acargo)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r24Recargos)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.r24CantidadAPagar)}</td>
                                                {/* IVA SC */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.ivaSimplificadoConfianzaAcargo)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.ivaSimplificadoConfianzaAfavor)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.ivaSimplificadoConfianzaRecargos)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.ivaSimplificadoConfianzaCantidadAPagar)}</td>
                                                {/* ISR SC */}
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.isrSimplificadoConfianzaAcargo)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.isrSimplificadoConfianzaRecargos)}</td>
                                                <td style={{ ...tdR, background:rowBg }}>{fmt(d?.isrSimplificadoConfianzaCantidadAPagar)}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    )
}
